import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Navbar, Footer } from '../../components/Layout/Layout';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Star, Loader2, CheckCircle } from 'lucide-react';

function StarRating({ value, onChange, label }) {
  var hoverState = useState(0);
  var hover = hoverState[0];
  var setHover = hoverState[1];

  return (
    <div className="space-y-1">
      <p className="text-sm text-slate-400">{label}</p>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(function(star) {
          return (
            <button
              key={star}
              type="button"
              onClick={function() { onChange(star); }}
              onMouseEnter={function() { setHover(star); }}
              onMouseLeave={function() { setHover(0); }}
              className="p-1 transition-colors"
            >
              <Star
                className={'h-6 w-6 ' + ((hover || value) >= star ? 'fill-amber-400 text-amber-400' : 'text-slate-600')}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ReviewPage() {
  var params = useParams();
  var token = params.token;
  var navigate = useNavigate();

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  var reviewInfoState = useState(null);
  var reviewInfo = reviewInfoState[0];
  var setReviewInfo = reviewInfoState[1];

  var submittingState = useState(false);
  var submitting = submittingState[0];
  var setSubmitting = submittingState[1];

  var submittedState = useState(false);
  var submitted = submittedState[0];
  var setSubmitted = submittedState[1];

  // Form state
  var overallState = useState(0);
  var overall = overallState[0];
  var setOverall = overallState[1];

  var communicationState = useState(0);
  var communication = communicationState[0];
  var setCommunication = communicationState[1];

  var timelinessState = useState(0);
  var timeliness = timelinessState[0];
  var setTimeliness = timelinessState[1];

  var professionalismState = useState(0);
  var professionalism = professionalismState[0];
  var setProfessionalism = professionalismState[1];

  var valueState = useState(0);
  var value = valueState[0];
  var setValue = valueState[1];

  var wouldUseAgainState = useState(true);
  var wouldUseAgain = wouldUseAgainState[0];
  var setWouldUseAgain = wouldUseAgainState[1];

  var reviewTextState = useState('');
  var reviewText = reviewTextState[0];
  var setReviewText = reviewTextState[1];

  useEffect(function() {
    api.getReviewTokenInfo(token).then(function(res) {
      setReviewInfo(res.data);
      setLoading(false);
    }).catch(function(err) {
      setError(err.response?.data?.detail || 'Invalid or expired review link');
      setLoading(false);
    });
  }, [token]);

  function handleSubmit(e) {
    e.preventDefault();
    if (overall === 0) {
      toast.error('Please provide an overall rating');
      return;
    }

    setSubmitting(true);
    api.createReview({
      review_token: token,
      overall_rating: overall,
      communication_rating: communication || null,
      timeliness_rating: timeliness || null,
      professionalism_rating: professionalism || null,
      value_rating: value || null,
      would_use_again: wouldUseAgain,
      review_text: reviewText || null
    }).then(function() {
      setSubmitted(true);
      toast.success('Thank you for your review!');
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
      setSubmitting(false);
    });
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col noise-overlay">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col noise-overlay">
        <Navbar />
        <main className="flex-1 flex items-center justify-center py-12">
          <Card className="card-base max-w-md mx-4">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                <Star className="h-8 w-8 text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Unable to Load Review</h2>
              <p className="text-slate-400">{error}</p>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col noise-overlay">
        <Navbar />
        <main className="flex-1 flex items-center justify-center py-12">
          <Card className="card-base max-w-md mx-4">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-teal-500/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-teal-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Thank You!</h2>
              <p className="text-slate-400 mb-6">Your review has been submitted successfully.</p>
              <Button onClick={function() { navigate('/'); }} className="btn-accent">
                Return to Home
              </Button>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col noise-overlay">
      <Navbar />
      <main className="flex-1 py-12">
        <div className="section-container max-w-2xl">
          <Card className="card-base">
            <CardHeader className="text-center border-b border-slate-800 pb-6">
              <CardTitle className="text-2xl text-white">Leave a Review</CardTitle>
              <p className="text-slate-400 mt-2">
                How was your experience with <span className="text-teal-400 font-medium">{reviewInfo.server_name}</span>
                {reviewInfo.server_company && <span className="text-slate-500"> ({reviewInfo.server_company})</span>}?
              </p>
            </CardHeader>
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Overall Rating - Required */}
                <div className="p-4 bg-slate-800/60 rounded-lg border border-slate-700">
                  <StarRating 
                    value={overall} 
                    onChange={setOverall} 
                    label="Overall Rating *" 
                  />
                </div>

                {/* Detailed Ratings - Optional */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-800/40 rounded-lg border border-slate-700/50">
                    <StarRating value={communication} onChange={setCommunication} label="Communication" />
                  </div>
                  <div className="p-4 bg-slate-800/40 rounded-lg border border-slate-700/50">
                    <StarRating value={timeliness} onChange={setTimeliness} label="Timeliness" />
                  </div>
                  <div className="p-4 bg-slate-800/40 rounded-lg border border-slate-700/50">
                    <StarRating value={professionalism} onChange={setProfessionalism} label="Professionalism" />
                  </div>
                  <div className="p-4 bg-slate-800/40 rounded-lg border border-slate-700/50">
                    <StarRating value={value} onChange={setValue} label="Value for Money" />
                  </div>
                </div>

                {/* Would Use Again */}
                <div className="flex items-center gap-3 p-4 bg-slate-800/40 rounded-lg border border-slate-700/50">
                  <input
                    type="checkbox"
                    id="wouldUseAgain"
                    checked={wouldUseAgain}
                    onChange={function(e) { setWouldUseAgain(e.target.checked); }}
                    className="h-5 w-5 rounded border-slate-600 bg-slate-900 text-teal-500 focus:ring-teal-500"
                  />
                  <label htmlFor="wouldUseAgain" className="text-slate-300">I would use this process server again</label>
                </div>

                {/* Written Review */}
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Your Review (Optional)</label>
                  <Textarea
                    value={reviewText}
                    onChange={function(e) { setReviewText(e.target.value); }}
                    placeholder="Share your experience..."
                    className="bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-500 min-h-[120px]"
                  />
                </div>

                <Button type="submit" className="w-full btn-accent" disabled={submitting}>
                  {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Submit Review
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default ReviewPage;
